import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, Input } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { Row } from '@tanstack/react-table';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { TableContentTransformations } from 'components/ReorderResizeTable/types';
import SingleSelectDropdownFilter from 'components/SingleSelectDropdownFilter';
import SearchIcon from 'icons/Search.icon';
import WarningExclamationIcon from 'icons/WarningExclamation.icon';
import confirm from 'modules/confirm';
import { getArrayLength, getHasElementsInArray } from 'utils';
import {
  PARSED_SPREADSHEET_RFI_ROW_STATUS,
  ParsedSpreadsheetRFIRow,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { RFI_LIST_STATUS_FILTER_ALL } from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import {
  filterParsedSpreadsheetRows,
  getReadyDocumentsCount,
  sortParsedSpreadsheetRows,
} from '../../../../utils';
import {
  BODY_CONTENT_GENERATE,
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  ParsedRow,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './data';
import EditRowForm from './components/EditRowForm';

import './ParsedDocumentsTable.styles.scss';

type ParsedDocumentsTableProps = {
  className?: string;
  rows: ParsedSpreadsheetRFIRow[];
  onRowsChange: (rows: ParsedSpreadsheetRFIRow[]) => void;
};

const ParsedDocumentsTable: React.FC<ParsedDocumentsTableProps> = (props) => {
  const { className, rows, onRowsChange } = props;

  const { t } = useTranslation();

  const [statusFilter, setStatusFilter] = useState<string>(
    RFI_LIST_STATUS_FILTER_ALL,
  );
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<string>();
  const [editingRow, setEditingRow] = useState<ParsedSpreadsheetRFIRow>();

  const readyCount = useMemo(() => getReadyDocumentsCount(rows), [rows]);

  const needsAttentionCount = useMemo(
    () => getArrayLength(rows) - readyCount,
    [rows, readyCount],
  );

  const statusOptions = useMemo(
    () => [
      {
        value: RFI_LIST_STATUS_FILTER_ALL,
        label: t('allStatuses'),
      },
      {
        value: PARSED_SPREADSHEET_RFI_ROW_STATUS.READY_TO_ADD,
        label: t('readyToAddStatus'),
      },
      {
        value: PARSED_SPREADSHEET_RFI_ROW_STATUS.NEEDS_ATTENTION,
        label: t('needsAttentionStatus'),
      },
    ],
    [t],
  );

  const statusFilterTitle = useMemo(
    () => statusOptions.find(({ value }) => value === statusFilter)?.label,
    [statusOptions, statusFilter],
  );

  const filteredRows = useMemo(
    () => filterParsedSpreadsheetRows(rows, statusFilter, search),
    [rows, statusFilter, search],
  );

  const sortedRows = useMemo(
    () => sortParsedSpreadsheetRows(filteredRows, sort),
    [filteredRows, sort],
  );

  const onReset = useCallback(async () => {
    if (
      await confirm({
        title: t('resetSpreadsheetTitle'),
        content: t('resetSpreadsheetDescription'),
        submitBtnText: t('resetSpreadsheet'),
        cancelBtnText: t('cancel'),
      })
    )
      onRowsChange([]);
  }, [t, onRowsChange]);

  const handleRowSave = useCallback(
    (updatedRow: ParsedSpreadsheetRFIRow) => {
      onRowsChange(rows.map((row) => (row === editingRow ? updatedRow : row)));
      setEditingRow(undefined);
    },
    [rows, editingRow, onRowsChange],
  );

  const tableContentTransformations = useMemo<
    TableContentTransformations<ParsedRow>
  >(() => {
    const generic = TABLE_COLUMNS.reduce((acc, col) => {
      const key = col as keyof typeof BODY_CONTENT_GENERATE;

      return {
        ...acc,
        [key]: (row: ParsedSpreadsheetRFIRow) =>
          BODY_CONTENT_GENERATE[key]({
            row,
            onClick: () => setEditingRow(row),
          }),
      };
    }, {} as TableContentTransformations<ParsedRow>);

    if (!editingRow) return generic;

    return {
      ...generic,
      document: (row: ParsedSpreadsheetRFIRow) =>
        row === editingRow ? (
          <EditRowForm
            row={editingRow}
            onSave={handleRowSave}
            onCancel={() => setEditingRow(undefined)}
          />
        ) : (
          generic.document(row)
        ),
    };
  }, [editingRow, handleRowSave]);

  const getColSpan = useCallback(
    (row: ParsedSpreadsheetRFIRow, col: string) =>
      row === editingRow && col === 'document' ? TABLE_COLUMNS.length : 1,
    [editingRow],
  );

  const getRowBackgroundColor = useCallback(
    (row: ParsedSpreadsheetRFIRow) =>
      row === editingRow ? 'parsed-documents-table__editing-row' : '',
    [editingRow],
  );

  const getIsRowExpanded = useCallback(
    (row: Row<ParsedRow>) =>
      row.original !== editingRow && getHasElementsInArray(row.original.errors),
    [editingRow],
  );

  const renderExpandedContent = useCallback(
    (row: ParsedRow) =>
      row.errors.map((error) => (
        <p key={error} className="parsed-documents-table__error">
          <WarningExclamationIcon />
          {error}
        </p>
      )),
    [],
  );

  const classes = classNames('parsed-documents-table', className);

  return (
    <div className={classes}>
      <div className="parsed-documents-table__summary-row">
        <p className="parsed-documents-table__summary">
          {t('foundDocumentsInSpreadsheetSummary', {
            total: getArrayLength(rows),
            ready: readyCount,
            needsAttention: needsAttentionCount,
          })}
        </p>

        <Button type="button" variant="outline" onClick={onReset}>
          {t('resetSpreadsheet')}
        </Button>
      </div>

      <div className="parsed-documents-table__filters">
        <SingleSelectDropdownFilter
          selectedValue={statusFilter}
          triggerTitle={statusFilterTitle}
          options={statusOptions}
          onChange={setStatusFilter}
        />

        <Input
          placeholder={t('searchDocuments')}
          prefixNode={<SearchIcon />}
          value={search}
          onChange={({ target: { value } }) => setSearch(value)}
        />
      </div>

      <ReorderResizeTable
        isDragDisabled
        isResizeDisabled
        recordName="document"
        columns={TABLE_COLUMNS}
        data={sortedRows as unknown as ParsedRow[]}
        headingTranslations={HEADING_TRANSLATIONS}
        tableHeaderContentTransformations={{ edit: () => null }}
        tableContentTransformations={tableContentTransformations}
        sortingValues={SORTING_VALUES}
        customSizes={CUSTOM_COL_SIZES}
        sort={sort}
        updateSort={setSort}
        getRowId={(row, index) => `${row.document}-${index}`}
        getColSpan={getColSpan}
        getRowBackgroundColor={getRowBackgroundColor}
        getIsRowExpanded={getIsRowExpanded}
        renderExpandedContent={renderExpandedContent}
      />
    </div>
  );
};

export default ParsedDocumentsTable;
